import React, { FunctionComponent, useEffect, useState } from 'react';
import { pathOr } from 'ramda';
import { useQuery } from '@apollo/react-hooks';
import { match } from 'react-router-dom';
import { Grid, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';

import {
  useMerchantOverview,
  KeyMetricView,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';
import {
  GET_VENUE_TYPE,
  GET_CASHBACK_SEGMENT,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/QuickLook/QuickLook.queries';
import { ClassProp } from 'helpers/types';
import { Theme } from 'config';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import { ChartPlaceholder } from 'components/Placeholder';
import Query from 'components/Query';
import { GET_MERCHANT } from 'components/Header/components/Filters/components/VenueTypeFilter/VenueTypeFilter.query';

import Transactions from './components/Transactions';
import Sales from './components/Sales';
import AOV from './components/AOV';
import Attribution from './components/Attribution';
import Frequency from './components/Frequency';
import { QueryResponse, getTimeslice } from './KeyMetricGraphs.helpers';
import { GET_CLOF_BASED_KEY_METRICS } from './KeyMetricGraphs.queries';

type Merchant = {
  hasOnlineVenue: boolean;
  hasNearbyVenue: boolean;
};

const keyMetricComponents = {
  Transactions,
  Sales,
  AOV,
  Attribution,
  Frequency,
};

const keyMetricTitles = {
  Transactions: 'Transaction Summary',
  Sales: 'Sales Summary',
  AOV: 'AOV - Daily Moving Average',
  Frequency: 'Frequency by Number of Visits',
  Lift: 'Lift - Daily Moving Average',
  Attribution: 'Attribution Rate - 30 days',
};

const keyMetricDataSource = {
  Transactions: 'clofBasedKeyMetrics',
  Sales: 'clofBasedKeyMetrics',
  AOV: 'clofBasedKeyMetrics',
  Frequency: 'purchasesBySubWithMulPurchases',
  Attribution: 'attributionByMerchant',
};

const styles = (theme: Theme) => ({
  grid: {
    position: 'relative',
  },
  headerBlock: {
    background: '#fff !important',
    padding: '2rem !important',
  },
  header: {
    color: `${theme.darkGray} !important`,
  },
  loading: {
    '&&': {
      margin: '0 -1rem !important',
    },
  },
});

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  match: match;
  classes: Styles;
};

const renderKeyMetricGraphs = (
  classes: Styles,
  keyMetricView: KeyMetricView,
  showOnline: boolean,
  cashbackPercentage?: string,
) => (response: QueryResponse) => {
  if (response.loading) {
    return <ChartPlaceholder className={classes.loading} />;
  }

  const GraphComponent = keyMetricComponents[keyMetricView];
  const graphTitle = keyMetricTitles[keyMetricView];
  const dataSource = keyMetricDataSource[keyMetricView];
  const timeslice = getTimeslice(response);

  return (
    <Grid className={classes.grid}>
      <Grid.Row className={classes.headerBlock}>
        <Header className={classes.header} as="h3">
          {graphTitle}
        </Header>
      </Grid.Row>

      <GraphComponent
        timeslice={timeslice}
        response={response}
        dataSource={dataSource}
        showOnline={showOnline}
        cashbackPercentage={cashbackPercentage}
      />
    </Grid>
  );
};

const KeyMetricGraphs: FunctionComponent<Props> = ({ classes, match }) => {
  const { keyMetricView, userProfile } = useMerchantOverview();
  const { startDate, endDate } = useDatePicker();
  const venueTypePayload = useQuery(GET_VENUE_TYPE);
  const { loading: loadingSegments, data: segmentData } = useQuery(
    GET_CASHBACK_SEGMENT,
  );
  const [cashbackPercentage, setCashbackPercentage] = useState<string>('');
  const [cashbackDollarAmt, setCashbackDollarAmt] = useState<string>('');

  let { merchantId, merchantName } = userProfile;

  merchantId = pathOr(merchantId, ['params', 'merchantId'], match);
  merchantName = pathOr(merchantName, ['params', 'merchantName'], match);

  const { loading, data } = useQuery(GET_MERCHANT, {
    variables: {
      params: {
        merchantName,
      },
    },
  });

  useEffect(() => {
    if (loadingSegments && !segmentData) return;
    const cashbackSegment = pathOr(
      'null|null',
      ['cashbackSegment'],
      segmentData,
    ).split('|');
    const type = cashbackSegment[0];
    const value = cashbackSegment[1];
    setCashbackPercentage(type === 'percent' ? value : null);
    setCashbackDollarAmt(type === 'dollar' ? value : null);
  }, [loadingSegments, segmentData]);

  if (loading) return null;
  const { hasNearbyVenue, hasOnlineVenue } = pathOr(
    [],
    ['merchantList', 'result', '0'],
    data,
  ) as Merchant;
  const showOnline = hasNearbyVenue && hasOnlineVenue;
  const venueType = pathOr(null, ['data', 'venueType'], venueTypePayload);

  return (
    <Query
      enableProgressInfo
      query={GET_CLOF_BASED_KEY_METRICS}
      variables={{
        params: {
          startDate,
          endDate,
          merchantId,
          merchantName,
          venueType,
          cashbackPercentage: parseInt(cashbackPercentage, 10),
          cashbackDollarAmt: parseInt(cashbackDollarAmt, 10),
        },
        attributionParams: {
          startDate,
          endDate,
          merchantName,
        },
      }}
      render={renderKeyMetricGraphs(
        classes,
        keyMetricView,
        showOnline,
        cashbackPercentage,
      )}
    />
  );
};

export default withStyles(styles)(KeyMetricGraphs);
